import { Document } from '../types';
import { Download, Edit, Eye, MoreVertical, Trash2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { canPreview } from '../utils';

interface DocumentActionsProps {
  document: Document;
  onPreview?: (uuid: string) => void;
  onDownload?: (document: Document) => void;
  onDelete?: (uuid: string) => void;
  onEdit?: (document: Document) => void;
}

export const DocumentActions: React.FC<DocumentActionsProps> = ({
  document,
  onPreview,
  onDownload,
  onDelete,
  onEdit,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Positionner le menu en dessous du bouton, aligné à droite
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 192, // 192px = w-48
      });
    }

    setIsOpen(!isOpen);
  };

  const dropdownMenu = isOpen && (
    <div
      ref={menuRef}
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
      }}
      className="w-48 rounded-lg border border-neutral-200 bg-white py-1 shadow-lg"
    >
      {document.mimeType && canPreview(document.mimeType) && onPreview && (
        <button
          onClick={e => {
            e.stopPropagation();
            onPreview(document.uuid);
            setIsOpen(false);
          }}
          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          <Eye className="h-4 w-4" />
          Prévisualiser
        </button>
      )}

      {onDownload && (
        <button
          onClick={e => {
            e.stopPropagation();
            onDownload(document);
            setIsOpen(false);
          }}
          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          <Download className="h-4 w-4" />
          Télécharger
        </button>
      )}

      {onEdit && (
        <button
          onClick={e => {
            e.stopPropagation();
            onEdit(document);
            setIsOpen(false);
          }}
          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          <Edit className="h-4 w-4" />
          Modifier
        </button>
      )}

      {onDelete && (
        <>
          <div className="my-1 border-t border-neutral-200" />
          <button
            onClick={e => {
              e.stopPropagation();
              onDelete(document.uuid);
              setIsOpen(false);
            }}
            className="text-error-600 hover:bg-error-50 flex w-full items-center gap-2 px-4 py-2 text-sm transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Supprimer
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="rounded-lg p-1 text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-neutral-100 hover:text-neutral-600"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {dropdownMenu && createPortal(dropdownMenu, window.document.body)}
    </>
  );
};
